import React from 'react';
import { Modal, Table, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { fetchAlertRuleHistory, AlertRuleHistoryEntry } from '@src/api/alertRules';
import CreatedBy from '@src/components/common/CreatedBy';
import { historyRuleAtom } from '../atoms';
import { useDimensionOptions, useMetricOptions } from '../hooks';
import { ACTION_COLORS, ACTION_LABELS, describeChanges, formatDateTime } from './utils';

const AlertRuleHistory = () => {
  const [historyRule, setHistoryRule] = useAtom(historyRuleAtom);
  const isOpen = historyRule !== null;
  const ruleId = historyRule?.id;

  const { resolveDimensionLabel } = useDimensionOptions();
  const { resolveMetricLabel } = useMetricOptions(isOpen);

  const {
    data: entries = [],
    isLoading,
    isError,
  } = useQuery(
    ['alert-rule-history', ruleId],
    (): Promise<AlertRuleHistoryEntry[]> =>
      fetchAlertRuleHistory(ruleId as number).then((response) => response.data?.data || []),
    {
      enabled: isOpen && ruleId !== undefined,
      refetchOnWindowFocus: false,
    },
  );

  const columns: ColumnsType<AlertRuleHistoryEntry> = [
    {
      title: 'Date & Time',
      key: 'changedAt',
      width: 180,
      render: (_k, row) => formatDateTime(row.changedAt),
    },
    {
      title: 'User',
      key: 'user',
      width: 200,
      render: (_k, row) => <CreatedBy name={row.changedByName} email={row.changedByEmail} />,
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_k, row) => (
        <Tag color={ACTION_COLORS[row.action]}>{ACTION_LABELS[row.action] || row.action}</Tag>
      ),
    },
    {
      title: 'Changes',
      key: 'changes',
      render: (_k, row) => {
        const lines = describeChanges(row, { resolveDimensionLabel, resolveMetricLabel });
        return (
          <ul style={{ margin: 0, paddingLeft: 16 }}>
            {lines.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        );
      },
    },
  ];

  return (
    <Modal
      open={isOpen}
      width="70vw"
      title={historyRule ? `Change history \u2014 ${historyRule.name}` : 'Change history'}
      footer={null}
      onCancel={() => setHistoryRule(null)}
      destroyOnClose
    >
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={entries}
        columns={columns}
        pagination={false}
        scroll={{ y: '55vh' }}
        locale={{ emptyText: isError ? 'Failed to load history' : 'No history yet' }}
      />
    </Modal>
  );
};

export default AlertRuleHistory;
